import React, { useState } from 'react'
import { Check, ChevronDown, ChevronUp, Package, Globe, Smartphone, Tablet, Laptop, Computer, MonitorSmartphone, Apple } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useChatStore } from '../askAnything/chatStore'
import { useRecentProductsStore } from '../askAnything/recentProductsStore'

export const SidebarProductSelector = () => {
  const { isDarkMode } = useTheme()
  const { products, selectedProduct, setSelectedProduct, isLoading } = useChatStore()
  const { addRecentProduct } = useRecentProductsStore()
  const [expanded, setExpanded] = useState(false)
  
  // Determine which products to show
  const initialProductCount = 3
  const visibleProducts = expanded ? products : products.slice(0, initialProductCount)
  const hasMoreProducts = products.length > initialProductCount
  
  // Function to get the appropriate platform icon
  const getPlatformIcon = (platform: string = '', size = 16) => {
    const iconClass = isDarkMode ? 'text-gray-400' : 'text-gray-500'
    
    switch (platform) {
      case 'iOS':
        return <Apple size={size} className={iconClass} />
      case 'iPad OS':
        return <Tablet size={size} className={iconClass} />
      case 'Android':
        return <Smartphone size={size} className={iconClass} />
      case 'Web':
        return <Globe size={size} className={iconClass} />
      case 'Mac OS':
        return <Laptop size={size} className={iconClass} />
      case 'Windows':
        return <Computer size={size} className={iconClass} />
      case 'Linux':
        return <Computer size={size} className={iconClass} />
      case 'Multi-Platform':
        return <MonitorSmartphone size={size} className={iconClass} />
      default:
        return <Package size={size} className={iconClass} />
    }
  }

  const handleProductSelect = (product: any) => {
    setSelectedProduct(product)
    addRecentProduct({
      id: product.id,
      name: product.name,
      icon: product.icon
    })
  }

  return (
    <div className="mb-4">
      <h2
        className={`text-sm uppercase tracking-wider font-semibold mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
      >
        Products
      </h2>
      <div className="space-y-1">
        {isLoading ? (
          <div className={`py-2 px-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
        ) : (
          <>
            {visibleProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductSelect(product)}
                className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer transition-colors ${selectedProduct?.id === product.id
                  ? isDarkMode
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-900'
                  : isDarkMode
                    ? 'hover:bg-gray-800 text-gray-300'
                    : 'hover:bg-gray-50 text-gray-700'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex-shrink-0 rounded overflow-hidden flex items-center justify-center
                    ${!product.logo ? (isDarkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}"
                  >
                    {product.logo ? (
                      <img
                        src={product.logo}
                        alt={`${product.name} logo`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getPlatformIcon(product.platform, 16)
                    )}
                  </div>
                  <span className="font-medium">{product.name}</span>
                </div>
                {selectedProduct?.id === product.id && (
                  <Check size={16} className="text-blue-500" />
                )}
              </div>
            ))}
            
            {hasMoreProducts && (
              <div
                onClick={() => setExpanded(!expanded)}
                className={`flex items-center justify-between px-3 py-2 rounded cursor-pointer transition-colors ${
                  isDarkMode
                    ? 'hover:bg-gray-800 text-gray-300'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 flex-shrink-0 rounded overflow-hidden flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <Package size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                  </div>
                  <span className="font-medium">
                    {expanded
                      ? 'Show less'
                      : `Show ${products.length - initialProductCount} more`}
                  </span>
                </div>
                {expanded ? (
                  <ChevronUp size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                ) : (
                  <ChevronDown size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SidebarProductSelector
